import { getCurrentLang } from 'services/contexts/LangContext'
import { getSupportedLangs, LocaleCode } from 'services/geo-management'
import { WithIdAndName } from 'types/common'
import { getName, mapValues } from '.'

const HTML_TAGS_REGEX = /<(.|\n)*?>/g

export const supportedLangs = getSupportedLangs()

export type SupportedLangsCode = string

export type MultiLanguageString = Record<SupportedLangsCode, string>

export type MultilangParseFunction = (v?: string) => MultiLanguageString
export type MultilangWriteFunction = (
  lang: SupportedLangsCode,
  v: string,
  parsed: MultiLanguageString
) => string | undefined

// JSON
// Parsing logic
export const parseMultilangJSON: MultilangParseFunction = (v) => {
  if (!v?.replace(HTML_TAGS_REGEX, '').trim()) {
    return supportedLangs.reduce<MultiLanguageString>(
      (acc, { localeCode }) => ({ ...acc, [localeCode]: '' }),
      {}
    )
  }

  if (typeof v === 'object') return v

  try {
    return JSON.parse(v) as MultiLanguageString
  } catch (error) {
    return supportedLangs.reduce<MultiLanguageString>(
      (acc, { localeCode }) => ({ ...acc, [localeCode]: v }),
      {}
    )
  }
}

// Writing logic
export const writeMultilangJSON: MultilangWriteFunction = (
  localeCode,
  v,
  parsed
) => {
  const updatedObject = {
    ...parsed,
    [localeCode]: v,
  }
  if (Object.values(updatedObject).every((v) => v === '')) {
    return ''
  } else {
    return JSON.stringify(updatedObject)
  }
}

export const parseMultilangUpsellingNames = <T extends WithIdAndName>(u: T[]) =>
  u.map(getName).map(parseMultilang(getCurrentLang())).join(', ')

export const parseMultilang =
  (lang: LocaleCode) =>
  <T extends unknown>(v: T) =>
    typeof v === 'string' ? parseMultilangJSON(v)[lang] : v

export const parseCsvFields = <T extends Record<string, string>>(
  fields: T
): T => {
  const currentLang = getCurrentLang()
  return mapValues(parseMultilang(currentLang), fields) as T
}

export const translateChoices = (
  choices: Object[],
  optionText: string,
  lang: LocaleCode
) =>
  choices?.map((c) => ({
    ...c,
    [optionText]: parseMultilang(lang)(
      (c as { [optionText: string]: string })[optionText]
    ),
  }))
